import MUIContainer from '@/components/MUI-Components/MUI-Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React, {memo} from 'react';
import parse from 'html-react-parser';
import {colors, fontSize, fontWeight, borderRadius, lineHeightRatio, transition} from '@/styles/tokens';

const tableContainerSx = {
    border: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: 3,
    overflow: 'hidden',
    mt: 6
};

const rowSx = (isLast: boolean) => ({
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', md: '220px 1fr 260px'},
    borderBottom: isLast ? 'none' : `1px solid ${colors.neutral.borderLight}`,
    bgcolor: colors.background.subtle,
    '&:hover': {
        bgcolor: colors.background.tag,
        '& .type-chip': {
            borderColor: colors.primary.main
        }
    },
    transition: `background ${transition.base}`
});

const colBaseSx = {
    p: {xs: 2.5, md: 3},
    borderRight: {md: `1px solid ${colors.neutral.borderLight}`}
};

const lastColSx = {
    p: {xs: 2.5, md: 3}
};

const chipSx = {
    display: 'inline-block',
    px: 1.5,
    py: 0.5,
    borderRadius: borderRadius.full,
    border: `1px solid ${colors.neutral.borderLight}`,
    bgcolor: colors.background.tag,
    fontSize: fontSize[14],
    color: colors.primary.deep,
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeightRatio.normal,
    transition: `border-color ${transition.base}`
};

const rowNumberSx = {
    color: colors.accent.orange,
    flexShrink: 0,
    fontWeight: fontWeight.bold,
    fontSize: fontSize[16],
    lineHeight: lineHeightRatio.snug
};

const rowTitleSx = {
    fontWeight: fontWeight.bold,
    fontSize: fontSize[16],
    color: colors.text.primary
};

const rowBodySx = {
    fontSize: fontSize[16],
    color: colors.text.primary,
    mb: 2
};

const designedForSx = {
    fontSize: fontSize[16],
    color: colors.primary.deep
};

interface ChatbotTypeItem {
    title: string;
    body: string;
    chips: string[];
    designedFor: string;
}

interface ChatbotTypesProps {
    heading: string;
    subheading?: string;
    items: ChatbotTypeItem[];
}

const ChatbotTypes = ({heading, subheading, items}: ChatbotTypesProps) => {
    return (
        <Box>
            <MUIContainer>
                <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={3}>
                    <Typography variant="h1" component="h2" mb={3} fontSize={{xs: 35, sm: 48}} className="font-headers">
                        {heading ? parse(heading) : ''}
                    </Typography>
                    {subheading && (
                        <Typography
                            variant="subtitle1"
                            component="p"
                            className="font-regular-text generative-transforming-subheading"
                            sx={{color: colors.text.primary}}
                        >
                            {parse(subheading)}
                        </Typography>
                    )}
                </Box>

                <Box sx={tableContainerSx}>
                    {items.map((item, i) => (
                        <Box key={i} sx={rowSx(i === items.length - 1)}>
                            <Box sx={colBaseSx}>
                                <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1.5}}>
                                    <Typography component="span" className="font-headers" sx={rowNumberSx}>
                                        {String(i + 1).padStart(2, '0')}
                                    </Typography>
                                    <Typography variant="h3" component="h3" className="font-headers" sx={rowTitleSx}>
                                        {item.title}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={colBaseSx}>
                                <Typography variant="body1" className="font-regular-text" sx={rowBodySx}>
                                    {parse(item.body)}
                                </Typography>
                                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                                    {item.chips.map((chip, ci) => (
                                        <Box key={ci} component="span" className="type-chip" sx={chipSx}>
                                            {chip}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>

                            <Box sx={lastColSx}>
                                <Typography variant="body2" className="font-regular-text" sx={designedForSx}>
                                    {parse(item.designedFor)}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(ChatbotTypes);
